import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APP_EVENTS,
  captureAppEvent,
  TABLE_CONTEXT,
} from 'src/services/posthogEvents';

import { Api } from 'src/models/Api.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from './useHandleHttpRequestError';

export default function useGetUsers() {
  const [users, setUsers] = useState<PageOfUser>({});
  const [isGettingUsers, setIsGettingUsers] = useState(false);

  const { t } = useTranslation(I18NextNS.COMMON);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const handleGetUsers = useCallback(
    async (
      pageSize?: number,
      pageNumber: number = 0,
      searchValues: { emailAddress: string; name: string } = {
        emailAddress: '',
        name: '',
      },
    ) => {
      try {
        setIsGettingUsers(true);

        const results = await Api.remote._get_users_advanced(
          [
            {
              key: 'role',
              operator: EntityCriteriaOperatorEnum.NotEqual,
              value: UserRoleEnum.Patient,
            },
            {
              key: 'emailAddress',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.emailAddress.trim(),
            },
            {
              key: 'name',
              operator: EntityCriteriaOperatorEnum.Like,
              value: searchValues.name.trim(),
            },
          ],
          {
            pageSize,
            pageNumber,
            sortBy: 'createdOn',
            sortOrder: 'DESC',
          },
        );

        const usersData = {
          content: results.data.content,
          totalElements: results.data.totalElements,
          totalPages: results.data.totalPages,
          size: pageSize,
          number: pageNumber,
        };

        setUsers(usersData);

        captureAppEvent(APP_EVENTS.USERS_LOADED, {
          table: TABLE_CONTEXT.USERS,
          count: results.data.content?.length ?? 0,
          page_number: pageNumber + 1,
          total_pages: results.data.totalPages,
          total_elements: results.data.totalElements,
          has_email: Boolean(searchValues.emailAddress.trim()),
          has_name: Boolean(searchValues.name.trim()),
        });

        return usersData;
      } catch (error) {
        showHttpRequestError(error, {
          messagePrefix: t('message.error.failedToLoadUsers'),
        });

        throw error;
      } finally {
        setIsGettingUsers(false);
      }
    },
    [showHttpRequestError, t],
  );

  return { users, handleGetUsers, isGettingUsers };
}
